import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  backfillForBacktest,
  cancelBacktest,
  countBacktests,
  createBacktest,
  listBacktests,
  rerunBacktest,
  updateBacktestNotes,
  type BacktestCreate,
  type BacktestStatus,
  type BacktestSummary,
  type SizingMode,
} from "../api/backtests";
import { getBackfill } from "../api/data";
import { getStrategy, listStrategies } from "../api/strategies";
import Card from "../components/Card";
import Button from "../components/Button";
import SymbolSearch, { type PickedInstrument } from "../components/SymbolSearch";
import {
  NumberInput,
  noAutocorrect,
  straightenJsonQuotes,
} from "../components/NumberInput";
import StrategyParamFields, {
  useParamsFieldsSource,
} from "../components/StrategyParamFields";
import { ApiError } from "../lib/api";
import { useDebouncedValue } from "../lib/useDebouncedValue";

/** A run FAILED for lack of stored data — offer a one-click backfill + re-run. */
function isNoBarsError(msg?: string | null): boolean {
  return !!msg && /no bars in store/i.test(msg);
}

const SIZING_MODES: { value: SizingMode; label: string; defaults: string }[] = [
  { value: "fixed_lots", label: "Fixed lots", defaults: `{"lots": 1}` },
  {
    value: "fixed_rupees",
    label: "Fixed rupees",
    defaults: `{"rupees": "10000"}`,
  },
  {
    value: "percent_of_capital",
    label: "Percent of capital",
    defaults: `{"pct": "10"}`,
  },
  {
    value: "atr_volatility_target",
    label: "ATR volatility target",
    defaults: `{"target_vol_pct": "2"}`,
  },
  {
    value: "risk_per_trade",
    label: "Risk per trade",
    defaults: `{"risk_pct": "1"}`,
  },
];

export default function BacktestsPage() {
  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-semibold">Backtests</h1>
      <Card title="Run a backtest">
        <BacktestForm />
      </Card>
      <Card title="Recent backtest runs">
        <RecentRunsTable />
      </Card>
    </div>
  );
}

function BacktestForm() {
  const qc = useQueryClient();
  const { data: strategies } = useQuery({
    queryKey: ["strategies"],
    queryFn: listStrategies,
  });

  const [strategyKey, setStrategyKey] = useState("");
  const [strategyParams, setStrategyParams] = useState("{}");
  // Track whether the user has manually edited the params textarea. If they
  // have, we won't clobber their edits when the strategy detail loads.
  const [paramsUserEdited, setParamsUserEdited] = useState(false);
  const [sizingMode, setSizingMode] = useState<SizingMode>("fixed_lots");
  const [sizingParams, setSizingParams] = useState(`{"lots": 1}`);

  // When a strategy is picked, fetch its detail to surface the default params.
  const strategyDetail = useQuery({
    queryKey: ["strategy", strategyKey],
    queryFn: () => getStrategy(strategyKey),
    enabled: !!strategyKey,
  });

  // Auto-prefill the params textarea on strategy change (unless the user has
  // already typed in it).
  function onStrategyChange(next: string) {
    setStrategyKey(next);
    setParamsUserEdited(false);
    // Reset to "{}" immediately; the effect below replaces it once the detail
    // loads from the cached or freshly-fetched query.
    setStrategyParams("{}");
  }

  useEffect(() => {
    if (paramsUserEdited) return;
    if (!strategyDetail.data) return;
    setStrategyParams(
      JSON.stringify(strategyDetail.data.default_params, null, 2),
    );
  }, [strategyDetail.data, paramsUserEdited]);

  // Best-effort parse of the params box, so the friendly fields below can read
  // it. On invalid JSON (mid-edit, say) we fall back to {} and every field
  // simply hides until the box parses again rather than guessing at values.
  const paramsObj = useMemo<Record<string, unknown>>(() => {
    try {
      const v = JSON.parse(strategyParams || "{}");
      return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }, [strategyParams]);

  // While the box is mid-edit and does not parse, the fields keep the last
  // values that did (locked) instead of the whole card disappearing.
  const paramsFieldsSource = useParamsFieldsSource(strategyParams);

  // Writing through a field counts as a manual edit: without this the prefill
  // effect above would re-run on the next strategy-detail read and throw the
  // change away.
  function patchParam(patch: Record<string, unknown>) {
    setStrategyParams(JSON.stringify({ ...paramsObj, ...patch }, null, 2));
    setParamsUserEdited(true);
  }

  const [symbol, setSymbol] = useState("");
  // The full pick, kept so the search box can show what is selected.
  const [picked, setPicked] = useState<PickedInstrument | null>(null);
  const [exchange, setExchange] = useState("DELTA");
  const [instrumentType, setInstrumentType] = useState("PERP");
  // Derivative identity — required for FUT/CE/PE (an Instrument won't build
  // without an expiry for those). strike only for options.
  const [expiry, setExpiry] = useState("");
  const [strike, setStrike] = useState("");
  const [lotSize, setLotSize] = useState("1");
  const [tickSize, setTickSize] = useState("0.05");
  const [timeframe, setTimeframe] = useState("1h");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [initialCapital, setInitialCapital] = useState("100000");
  const [margin, setMargin] = useState("1");
  const [slippageBps, setSlippageBps] = useState(5);
  // Off by default: turning it on changes what the numbers MEAN, so it must be
  // a deliberate choice rather than something that shifts saved results.
  const [includeCharges, setIncludeCharges] = useState(false);
  const [chargesBroker, setChargesBroker] = useState("zerodha");
  const [latencyMs, setLatencyMs] = useState(50);
  // Routed backtest ("trade like the deployment") — off by default.
  // "futures" trades the auto-rolled near-month contract itself; "option"
  // routes to the option chain with the future (or index) as the signal base.
  const [tradeOn, setTradeOn] = useState<
    "underlying" | "futures" | "option"
  >("underlying");
  // Base the signals run on for option-routed mode: the auto-rolled
  // near-month future (default — what a live deployment trades against) or
  // the root's cash index.
  const [baseInstrument, setBaseInstrument] = useState<"futures" | "index">(
    "futures",
  );
  const [optMoneyness, setOptMoneyness] = useState("ATM");
  const [itmDepth, setItmDepth] = useState("1");
  const [optPriceMin, setOptPriceMin] = useState("0");
  const [optPriceMax, setOptPriceMax] = useState("100000");
  const [optExpiryType, setOptExpiryType] = useState("weekly");
  const [error, setError] = useState<string | null>(null);

  function onSizingModeChange(next: SizingMode) {
    setSizingMode(next);
    const found = SIZING_MODES.find((m) => m.value === next);
    if (found) setSizingParams(found.defaults);
  }

  const create = useMutation({
    mutationFn: createBacktest,
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["backtests"] });
      qc.invalidateQueries({ queryKey: ["backtests-count"] });
    },
    onError: (e) => {
      setError(
        e instanceof ApiError
          ? typeof e.detail === "string"
            ? e.detail
            : JSON.stringify(e.detail)
          : "Failed",
      );
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!strategyKey) return setError("Pick a strategy.");
    if (!symbol || !startDate || !endDate)
      return setError("Symbol, start, and end are required.");
    let strategyParamsObj: Record<string, unknown> = {};
    let sizingParamsObj: Record<string, unknown> = {};
    try {
      strategyParamsObj = JSON.parse(strategyParams);
    } catch (e) {
      return setError(`Invalid strategy params JSON: ${(e as Error).message}`);
    }
    // Route to options like the deployment — nested so it rides in strategy_params.
    if (tradeOn === "futures") {
      strategyParamsObj = {
        ...strategyParamsObj,
        // No underlying override: fno_underlying_name auto-maps the index
        // symbol to its F&O root (NIFTY 50 -> NIFTY), same as the option path.
        __routing__: { order_instrument: "future" },
      };
    }
    if (tradeOn === "option") {
      strategyParamsObj = {
        ...strategyParamsObj,
        __routing__: {
          order_instrument: "option",
          base_instrument: baseInstrument,
          option_moneyness: optMoneyness,
          itm_depth: Number(itmDepth) || 1,
          price_min: Number(optPriceMin) || 0,
          price_max: Number(optPriceMax) || 100000,
          expiry_type: optExpiryType,
        },
      };
    }
    try {
      sizingParamsObj = JSON.parse(sizingParams);
    } catch (e) {
      return setError(`Invalid sizing params JSON: ${(e as Error).message}`);
    }

    const payload: BacktestCreate = {
      strategy_key: strategyKey,
      strategy_params: strategyParamsObj,
      sizing_mode: sizingMode,
      sizing_params: sizingParamsObj,
      symbol,
      exchange,
      instrument_type: instrumentType,
      // Expiry/strike belong to derivatives only — a leftover value from a
      // previous FUT/option selection must not ride along on an INDEX/EQ run
      // (it poisons the bars lookup key on the detail chart).
      ...(["FUT", "CE", "PE"].includes(instrumentType) && expiry ? { expiry } : {}),
      ...(["CE", "PE"].includes(instrumentType) && strike ? { strike } : {}),
      // Only derivatives carry a contract size. Sending a stale lot/tick from
      // a previous FUT selection on an EQ/INDEX run silently mis-sizes it —
      // the inputs are hidden for those types, so it was invisible.
      ...(["FUT", "CE", "PE"].includes(instrumentType)
        ? { lot_size: Number(lotSize) || 1, tick_size: tickSize || "0.05" }
        : { lot_size: 1, tick_size: "0.05" }),
      timeframe,
      start: new Date(startDate).toISOString(),
      end: new Date(endDate).toISOString(),
      initial_capital: initialCapital,
      margin,
      slippage_bps: slippageBps,
      include_charges: includeCharges,
      charges_broker: chargesBroker,
      latency_ms: latencyMs,
    };
    create.mutate(payload);
  }

  function onClear() {
    setStrategyKey("");
    setStrategyParams("{}");
    setParamsUserEdited(false);
    setSizingMode("fixed_lots");
    setSizingParams(`{"lots": 1}`);
    // Clear the whole instrument, not just its symbol — leaving the exchange,
    // type, expiry, strike and lot behind meant a "cleared" form still carried
    // the previous contract's identity.
    setSymbol("");
    setPicked(null);
    setExpiry("");
    setStrike("");
    setLotSize("1");
    setTickSize("0.05");
    setStartDate("");
    setEndDate("");
    setInitialCapital("100000");
    setMargin("1");
    setSlippageBps(5);
    setLatencyMs(50);
    setError(null);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Strategy">
          <select
            value={strategyKey}
            onChange={(e) => onStrategyChange(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
          >
            <option value="">— pick one —</option>
            {strategies?.map((s) => (
              <option key={s.key} value={s.key}>
                {s.display_name} ({s.key})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Sizing mode">
          <select
            value={sizingMode}
            onChange={(e) => onSizingModeChange(e.target.value as SizingMode)}
            className="w-full border border-border rounded px-3 py-2"
          >
            {SIZING_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Strategy params (JSON)">
          <textarea
            value={strategyParams}
            onChange={(e) => {
              setStrategyParams(straightenJsonQuotes(e.target));
              setParamsUserEdited(true);
            }}
            {...noAutocorrect}
            rows={3}
            className="w-full border border-border rounded px-3 py-2 font-mono text-base sm:text-xs"
            placeholder='{"fast_period": 10, "slow_period": 30}'
          />
          {strategyKey && strategyDetail.data && !paramsUserEdited && (
            <div className="text-xs text-muted mt-1">
              Prefilled with the strategy's defaults. Edit values to override.
            </div>
          )}
          {strategyKey &&
            strategyDetail.data &&
            Object.keys(strategyDetail.data.default_params).length === 0 && (
              <div className="text-xs text-amber-700 dark:text-warn-400 mt-1">
                This strategy has required params with no defaults — fill them
                in (see the catalog Strategy page for the JSON schema).
              </div>
            )}
        </Field>
        <Field label="Sizing params (JSON)">
          <textarea
            value={sizingParams}
            onChange={(e) => setSizingParams(straightenJsonQuotes(e.target))}
            {...noAutocorrect}
            rows={3}
            className="w-full border border-border rounded px-3 py-2 font-mono text-base sm:text-xs"
          />
        </Field>
      </div>

      <StrategyParamFields
        {...paramsFieldsSource}
        patch={patchParam}
        context="backtest"
        tone="muted"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Symbol">
          {/* Stored-only: a backtest needs history, so offering a symbol with
              no bars would just produce a run that fails. Picking one fills
              exchange, type, expiry, strike, lot and tick together, which also
              stops a stale lot/tick from a previous F&O pick riding along. */}
          <SymbolSearch
            mode="stored"
            value={picked ? { id: picked.id, display: picked.display } : null}
            onChange={(inst) => {
              setPicked(inst);
              setSymbol(inst.symbol);
              setExchange(inst.exchange);
              setInstrumentType(inst.instrument_type);
              setExpiry(inst.expiry ?? "");
              setStrike(inst.strike ?? "");
              setLotSize(String(inst.lot_size ?? 1));
              setTickSize(inst.tick_size ?? "0.05");
            }}
          />
        </Field>
        <Field label="Exchange">
          <select
            value={exchange}
            onChange={(e) => setExchange(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
          >
            <option>NSE</option>
            <option>BSE</option>
            <option>NFO</option>
            <option>BFO</option>
            <option>DELTA</option>
            <option>BINANCE</option>
            <option>FX</option>
          </select>
        </Field>
        <Field label="Type">
          <select
            value={instrumentType}
            onChange={(e) => setInstrumentType(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
          >
            <option>EQ</option>
            <option>FUT</option>
            <option>CE</option>
            <option>PE</option>
            <option>INDEX</option>
            <option>PERP</option>
            <option>SPOT</option>
          </select>
        </Field>
      </div>

      {["FUT", "CE", "PE"].includes(instrumentType) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Expiry (YYYY-MM-DD)">
            <input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="w-full border border-border rounded px-3 py-2"
            />
          </Field>
          {["CE", "PE"].includes(instrumentType) && (
            <Field label="Strike">
              <input
                type="number"
                value={strike}
                onChange={(e) => setStrike(e.target.value)}
                className="w-full border border-border rounded px-3 py-2"
              />
            </Field>
          )}
          <Field label="Lot size">
            <input
              type="number"
              value={lotSize}
              onChange={(e) => setLotSize(e.target.value)}
              className="w-full border border-border rounded px-3 py-2"
            />
          </Field>
          <Field label="Tick size">
            <input
              type="number"
              step="0.05"
              value={tickSize}
              onChange={(e) => setTickSize(e.target.value)}
              className="w-full border border-border rounded px-3 py-2"
            />
          </Field>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Timeframe">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
          >
            <option>1m</option>
            <option>3m</option>
            <option>5m</option>
            <option>10m</option>
            <option>15m</option>
            <option>30m</option>
            <option>1h</option>
            <option>1d</option>
          </select>
        </Field>
        <Field label="Trade on">
          <select
            value={tradeOn}
            onChange={(e) =>
              setTradeOn(
                e.target.value as "underlying" | "futures" | "option",
              )
            }
            className="w-full border border-border rounded px-3 py-2"
          >
            <option value="underlying">Underlying (as-is)</option>
            <option value="futures">Near-month futures (auto-roll)</option>
            <option value="option">Option (like the deployment)</option>
          </select>
        </Field>
        {tradeOn === "futures" && (
          <div className="md:col-span-2 rounded border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            <p>
              Trades the near-month contract that was live on each day,
              switching to the next contract after every expiry.{" "}
              <strong className="text-foreground">
                Any open position is force-closed on the expiring contract&rsquo;s
                last bar
              </strong>{" "}
              — exactly as you would have to do in the market. Positions are
              never carried across contracts, so the price gap between two
              contracts never shows up as profit or loss. These exits appear in
              the trade list tagged <code>expiry_close</code>.
            </p>
            <p className="mt-1">
              Intraday strategies are unaffected — they are already flat daily.
              Strategies that hold overnight will be flattened roughly once a
              month, so their results are{" "}
              <strong className="text-foreground">not comparable</strong> to the
              same strategy run on the index.
            </p>
            <p className="mt-1">
              Futures history is shorter than the index history — backfill with{" "}
              <code>scripts/backfill_nifty_futures.py</code> if a window returns
              no bars.
            </p>
          </div>
        )}
        {tradeOn === "option" && (
          <>
            <Field label="Deploy on (base)">
              <select
                value={baseInstrument}
                onChange={(e) =>
                  setBaseInstrument(e.target.value as "futures" | "index")
                }
                className="w-full border border-border rounded px-3 py-2"
              >
                <option value="futures">Near-month futures (auto-roll)</option>
                <option value="index">Cash index (e.g. NIFTY 50)</option>
              </select>
            </Field>
            <Field label="Option moneyness">
              <select
                value={optMoneyness}
                onChange={(e) => setOptMoneyness(e.target.value)}
                className="w-full border border-border rounded px-3 py-2"
              >
                <option value="ATM">ATM</option>
                <option value="ITM">ITM</option>
                <option value="price_range">Price range</option>
              </select>
            </Field>
            {optMoneyness === "ITM" && (
              <Field label="ITM depth (strikes)">
                <input
                  type="number"
                  value={itmDepth}
                  onChange={(e) => setItmDepth(e.target.value)}
                  className="w-full border border-border rounded px-3 py-2"
                />
              </Field>
            )}
            {optMoneyness === "price_range" && (
              <>
                <Field label="Premium min">
                  <input
                    type="number"
                    value={optPriceMin}
                    onChange={(e) => setOptPriceMin(e.target.value)}
                    className="w-full border border-border rounded px-3 py-2"
                  />
                </Field>
                <Field label="Premium max">
                  <input
                    type="number"
                    value={optPriceMax}
                    onChange={(e) => setOptPriceMax(e.target.value)}
                    className="w-full border border-border rounded px-3 py-2"
                  />
                </Field>
              </>
            )}
            <Field label="Expiry">
              <select
                value={optExpiryType}
                onChange={(e) => setOptExpiryType(e.target.value)}
                className="w-full border border-border rounded px-3 py-2"
              >
                <option value="weekly">Nearest (weekly)</option>
                <option value="monthly">Nearest monthly</option>
              </select>
            </Field>
          </>
        )}
        <Field label="From (date)">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
            required
          />
        </Field>
        <Field label="To (date)">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
            required
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Initial capital">
          <input
            value={initialCapital}
            onChange={(e) => setInitialCapital(e.target.value)}
            inputMode="decimal"
            className="w-full border border-border rounded px-3 py-2 font-mono"
          />
        </Field>
        <Field label="Margin (×)">
          <input
            type="number"
            min={1}
            max={100}
            step="0.5"
            title="Leverage multiplier; sizer sees capital × margin"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            className="w-full border border-border rounded px-3 py-2 font-mono"
          />
        </Field>
        <Field label="Slippage (bps)">
          <NumberInput
            min={0}
            value={slippageBps}
            onChange={setSlippageBps}
            className="w-full border border-border rounded px-3 py-2 font-mono"
          />
        </Field>
        <Field label="Brokerage & charges">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={includeCharges}
                onChange={(e) => setIncludeCharges(e.target.checked)}
                className="rounded border-border"
              />
              <span>Include brokerage, STT, GST and exchange fees</span>
            </label>
            {includeCharges && (
              <select
                value={chargesBroker}
                onChange={(e) => setChargesBroker(e.target.value)}
                className="w-full border border-border rounded px-3 py-2"
              >
                <option value="zerodha">Zerodha rates</option>
                <option value="dhan">Dhan rates</option>
                <option value="upstox">Upstox rates</option>
                <option value="delta">Delta Exchange rates</option>
              </select>
            )}
            <p className="text-xs text-muted">
              {includeCharges
                ? "P&L will be net of costs. Verify the rate card against a contract note on the Brokers page."
                : "P&L will be gross. For a strategy doing several round trips a day, costs are material."}
            </p>
          </div>
        </Field>
        <Field label="Latency (ms)">
          <NumberInput
            min={0}
            value={latencyMs}
            onChange={setLatencyMs}
            className="w-full border border-border rounded px-3 py-2 font-mono"
          />
        </Field>
      </div>

      {error && (
        <div className="text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2 whitespace-pre-wrap">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? "Starting…" : "Start backtest"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={create.isPending}
          onClick={onClear}
        >
          Clear form
        </Button>
      </div>
    </form>
  );
}

const PAGE_SIZE = 25;

const STATUS_OPTIONS: BacktestStatus[] = [
  "PENDING",
  "RUNNING",
  "SUCCESS",
  "FAILED",
  "CANCELLED",
];

function RecentRunsTable() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<BacktestStatus | "">("");
  const [page, setPage] = useState(0);
  // Search hits the server, so debounce it — one request per pause, not per
  // keystroke.
  const q = useDebouncedValue(search.trim(), 250);

  // A filter change re-slices the whole result set, so page 3 of the old
  // filter is meaningless under the new one — and would show an empty table
  // for a search that actually matched.
  useEffect(() => {
    setPage(0);
  }, [q, status]);

  const filters = { q, status };
  const { data: runs, isLoading } = useQuery({
    queryKey: ["backtests", q, status, page],
    queryFn: () => listBacktests({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    refetchInterval: 3_000,
    // Without this the table blanks on every page/search change and, with a
    // 3s poll behind it, flickers.
    placeholderData: keepPreviousData,
  });
  const { data: total } = useQuery({
    queryKey: ["backtests-count", q, status],
    queryFn: () => countBacktests(filters),
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
  });
  const cancel = useMutation({
    mutationFn: cancelBacktest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backtests"] });
      qc.invalidateQueries({ queryKey: ["backtests-count"] });
    },
  });

  const filtering = !!q || !!status;
  const from = page * PAGE_SIZE;
  const shown = runs?.length ?? 0;
  const hasNext = total != null ? from + shown < total : shown === PAGE_SIZE;

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search id, strategy, symbol, timeframe, note…"
        className="flex-1 w-full sm:min-w-[240px] text-sm px-3 py-2 rounded border border-border
          bg-surface placeholder:text-muted/70 focus:outline-none focus:ring-1
          focus:ring-brand-500"
      />
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value as BacktestStatus | "")}
        className="text-sm border border-border rounded px-3 py-2 bg-surface"
      >
        <option value="">All statuses</option>
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      {filtering && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSearch("");
            setStatus("");
          }}
        >
          Clear
        </Button>
      )}
    </div>
  );

  const pager = (
    <div className="flex items-center justify-between gap-3 pt-1">
      <div className="text-xs text-muted">
        {total != null && total > 0
          ? `Showing ${from + 1}–${from + shown} of ${total}`
          : total === 0
            ? "No matches"
            : ""}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={page === 0}
          onClick={() => setPage((n) => Math.max(0, n - 1))}
        >
          ← Newer
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!hasNext}
          onClick={() => setPage((n) => n + 1)}
        >
          Older →
        </Button>
      </div>
    </div>
  );

  if (isLoading && !runs)
    return (
      <div className="space-y-3">
        {controls}
        <div className="text-sm text-muted">Loading…</div>
      </div>
    );

  if (!runs || runs.length === 0)
    return (
      <div className="space-y-3">
        {controls}
        <div className="text-sm text-muted">
          {filtering
            ? "No backtests match this search."
            : "No backtests yet."}
        </div>
      </div>
    );

  return (
    <div className="space-y-3">
      {controls}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted border-b border-border/60">
            <tr>
              <th className="py-2 pr-3">Created</th>
              <th className="py-2 pr-3">Strategy</th>
              <th className="py-2 pr-3">Instrument</th>
              <th className="py-2 pr-3">Timeframe</th>
              <th className="py-2 pr-3">Range</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3 text-right">Return</th>
              <th className="py-2 pr-3 text-right">Sharpe</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const inFlight =
                r.status === "PENDING" || r.status === "RUNNING";
              const range = `${new Date(r.start_ts + "Z").toLocaleDateString()} → ${new Date(
                r.end_ts + "Z",
              ).toLocaleDateString()}`;
              return (
                <tr key={r.id} className="border-b border-border/60 last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 font-mono">
                    {r.strategy_key}
                    <ParamsCell params={r.strategy_params} />
                    <NoteCell id={r.id} initial={r.notes} />
                  </td>
                  <td className="py-2 pr-3 font-mono">
                    {r.exchange}:{r.symbol}
                  </td>
                  <td className="py-2 pr-3 font-mono">{r.timeframe}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{range}</td>
                  <td className="py-2 pr-3">
                    <StatusPill status={r.status} />
                    {r.error_message && (
                      <div
                        className="text-xs text-down-700 dark:text-down-400 mt-1 max-w-[120px] truncate"
                        title={r.error_message}
                      >
                        {r.error_message}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-right text-xs">
                    {formatPct(r.return_pct)}
                  </td>
                  <td className="py-2 pr-3 font-mono text-right text-xs">
                    {formatNumber(r.sharpe, 2)}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap space-x-2">
                    <Link
                      to={`/backtests/${r.id}`}
                      className="text-xs text-up-700 dark:text-up-400 hover:underline"
                    >
                      View
                    </Link>
                    {inFlight && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(r.id)}
                      >
                        Cancel
                      </Button>
                    )}
                    <BacktestFixActions run={r} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pager}
    </div>
  );
}

/**
 * For a run that FAILED because its data wasn't in the store: a "Backfill"
 * button that enqueues the data, polls the job, and swaps to "Run now" (which
 * re-runs the backtest) once the backfill succeeds.
 */
function BacktestFixActions({ run }: { run: BacktestSummary }) {
  const navigate = useNavigate();
  const [jobId, setJobId] = useState<string | null>(null);

  const backfill = useMutation({
    mutationFn: () => backfillForBacktest(run.id),
    onSuccess: (job) => setJobId(job.id),
    onError: (e) =>
      alert(
        e instanceof ApiError
          ? `Backfill failed: ${String(e.detail ?? e.message)}`
          : "Backfill failed",
      ),
  });

  const job = useQuery({
    queryKey: ["backfill", jobId],
    queryFn: () => getBackfill(jobId!),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "SUCCESS" || s === "FAILED" || s === "CANCELLED" ? false : 2000;
    },
  });

  const rerun = useMutation({
    mutationFn: () => rerunBacktest(run.id),
    onSuccess: (nw) => navigate(`/backtests/${nw.id}`),
  });

  if (run.status !== "FAILED" || !isNoBarsError(run.error_message)) return null;

  const jobStatus = job.data?.status;

  if (jobStatus === "SUCCESS") {
    return (
      <Button
        variant="primary"
        size="sm"
        disabled={rerun.isPending}
        onClick={() => rerun.mutate()}
        title="Data is ready — re-run this backtest"
      >
        {rerun.isPending ? "Starting…" : "Run now"}
      </Button>
    );
  }
  if (jobStatus === "FAILED" || jobStatus === "CANCELLED") {
    return (
      <Button
        variant="danger"
        size="sm"
        onClick={() => {
          setJobId(null);
          backfill.mutate();
        }}
      >
        Backfill failed — retry
      </Button>
    );
  }
  if (jobId) {
    return <span className="text-xs text-muted">Backfilling…</span>;
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={backfill.isPending}
      onClick={() => backfill.mutate()}
      title="Download the missing data for this run"
    >
      {backfill.isPending ? "Starting…" : "Backfill"}
    </Button>
  );
}

function StatusPill({ status }: { status: BacktestStatus }) {
  const map: Record<BacktestStatus, string> = {
    PENDING: "bg-surface-2 text-fg-subtle",
    RUNNING: "bg-amber-100 dark:bg-warn-500/15 text-warn-700 dark:text-warn-400",
    SUCCESS: "bg-emerald-100 dark:bg-up-500/15 text-up-700 dark:text-up-400",
    FAILED: "bg-red-100 dark:bg-down-500/15 text-down-700 dark:text-down-400",
    CANCELLED: "bg-surface-2 text-fg-subtle",
  };
  return (
    <span className={`inline-block text-xs font-mono px-2 py-0.5 rounded ${map[status]}`}>
      {status}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-subtle mb-1">{label}</label>
      {children}
    </div>
  );
}

function formatParams(p: Record<string, unknown>): string {
  const entries = Object.entries(p);
  if (entries.length === 0) return "(defaults)";
  return entries.map(([k, v]) => `${k}=${formatValue(v)}`).join(", ");
}

/** Params as ONE truncated line (the full dump made the list unreadable);
 * click to expand to the wrapped full text, click again to collapse. */
function ParamsCell({ params }: { params: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const text = formatParams(params);
  return (
    <div
      className={`text-xs text-muted font-normal cursor-pointer select-text ${
        expanded
          ? "max-w-md whitespace-pre-wrap break-words"
          : "max-w-[260px] truncate"
      }`}
      title={expanded ? "Click to collapse" : "Click to expand parameters"}
      onClick={() => setExpanded((e) => !e)}
    >
      {text}
    </div>
  );
}

/** Inline per-run note, shown under the strategy/params text. An explicit
 * Save button appears as soon as the text differs from the stored note (Enter
 * also saves; Escape reverts). Local state is seeded once so the list's 3s
 * refetch can't clobber in-progress typing. */
function NoteCell({ id, initial }: { id: string; initial: string | null }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initial ?? "");
  const [savedValue, setSavedValue] = useState(initial ?? "");
  const save = useMutation({
    mutationFn: (notes: string) => updateBacktestNotes(id, notes),
    onSuccess: (run) => {
      setSavedValue(run.notes ?? "");
      qc.invalidateQueries({ queryKey: ["backtests"] });
    },
  });
  const dirty = value.trim() !== savedValue.trim();
  const commit = () => {
    if (dirty && !save.isPending) save.mutate(value.trim());
  };
  return (
    <div className="mt-1 flex items-center gap-1 font-normal">
      <input
        type="text"
        value={value}
        placeholder="Add note…"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setValue(savedValue);
        }}
        disabled={save.isPending}
        title={value || "Note for this run"}
        className="w-44 text-xs px-2 py-1 rounded border border-border bg-surface
          placeholder:text-muted/60 focus:outline-none focus:ring-1
          focus:ring-up-500 disabled:opacity-60"
      />
      {dirty && (
        <button
          type="button"
          onClick={commit}
          disabled={save.isPending}
          className="text-xs px-2 py-1 rounded bg-up-600 text-white
            hover:bg-up-700 disabled:opacity-60"
        >
          {save.isPending ? "…" : "Save"}
        </button>
      )}
      {save.isError && (
        <span
          className="text-xs text-down-700 dark:text-down-400"
          title="Save failed — is the backend up to date? Restart it and retry."
        >
          failed
        </span>
      )}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v == null) return "-";
  if (typeof v === "number" || typeof v === "string" || typeof v === "boolean") {
    return String(v);
  }
  return JSON.stringify(v);
}

function formatPct(v: string | null): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return `${n.toFixed(2)}%`;
}

function formatNumber(v: string | null, digits = 2): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toFixed(digits);
}
